import React, { useEffect } from 'react';
import TopBar from 'components/TopBar/TopBar';
import GirlStudying from 'assets/girl-studying.svg';
import Software from 'assets/software.svg';
import Lecture from 'assets/lecture.svg';
import Price from 'assets/price.svg';
import Mail from 'assets/mail.svg';
import {
	Wrapper,
	FeaturesWrapper,
	Feature,
	FeatureImage,
	FeatureDescription,
	FeatureTitle,
	FeatureContent,
	Hero,
	HeroImage,
	CatalogueLink,
	Section,
	SectionInner,
	SectionBody,
	SectionHeader,
	SectionEyebrow,
	SectionTitle,
	SectionSubtitle,
	Grid,
	Item,
	ItemTitle,
	ItemText,
	PlatformNote,
} from './styled';
import theme from 'theme/theme';
import Button from 'components/Button/Button';
import StoreBadges from 'components/StoreBadges/StoreBadges';
import Copyright from 'components/Copyright/Copyright';
import ContactForm from 'components/ContactForm/ContactForm';
import routes from 'routes/routes';
import { redirectToAppIfSignedIn } from 'services/session';


/**
 * Landing highlights: the few capabilities a university weighs first. The full
 * catalogue lives on the Why Flass page — keep this list short on purpose.
 */
const HIGHLIGHTS = {
	eyebrow: 'What you get',
	title: 'Built for how a university actually runs',
	subtitle:
		'Course rooms, timetabled support, and the teaching tools staff use every week — in one place students already have on their phone.',
	features: [
		{
			title: 'A room for every module',
			text:
				'Modules, cohorts, and societies each get their own space, and an entire intake can be provisioned in one step rather than created by hand.',
		},
		{
			title: 'Office hours with a queue',
			text:
				'Open a session and students join a line. Staff take the next person in order, and everyone can see where they stand.',
		},
		{
			title: 'Teaching tools in the conversation',
			text:
				'Run a poll to check understanding, collect module feedback with a survey, and publish events without sending anyone elsewhere.',
		},
		{
			title: 'End-to-end encrypted',
			text:
				'Messages are encrypted under per-room keys that are only ever unwrapped on a signed-in device, so contents are not readable on the server.',
		},
		{
			title: 'No new account',
			text:
				'Students and staff sign in with their university email, so access follows the same joiner and leaver process as everything else.',
		},
		{
			title: 'Web, iOS, and Android',
			text:
				'A question asked in a lecture is answered from a phone on the way home, with per-room notification settings so nobody mutes everything.',
		},
	],
};

const Landing = (props) => {
	useEffect(() => {
		document.title = 'Home | Flass';
	});

	// A returning signed-in visitor almost certainly wants the app, not the
	// pitch. Only the landing page redirects — pricing and Why Flass stay
	// readable while signed in, and ?stay=1 opts out here too.
	useEffect(() => {
		redirectToAppIfSignedIn();
	}, []);
	const contactFormRef = React.useRef(null);
	const onContact = () => {
		contactFormRef.current.scrollIntoView();
	};
	return (
		<Wrapper>
			<TopBar />
			<Hero>
					<FeatureContent
						style={{
							width: '100%',
							maxWidth: '480px',
						}}>
						<FeatureTitle
							style={{
								// Fluid rather than a flat 40px: an inline
								// style outranks the component's media
								// queries, so the only way to keep the
								// headline inside a 320px screen is to make
								// the value itself responsive. Caps at 40px,
								// so anything 364px and wider is unchanged.
								fontSize: 'clamp(30px, 11vw, 40px)',
								color: theme.primary1,
							}}>
							Communication made easier.
						</FeatureTitle>
						<FeatureDescription
							style={{
								fontSize: '18px',
							}}>
							One secure place for course rooms, office hours,
							events, and everything students and staff need to
							reach each other — on the web, iOS, and Android.
						</FeatureDescription>
						<Button
							onClick={() => onContact()}
							style={{ marginTop: '10px' }}
							size='medium'>
							Reach Us Now
						</Button>
						{/* Sits under the primary CTA rather than beside it:
						    the institution is the customer here, so "Reach Us"
						    stays the lead and the student-facing app links
						    follow. */}
						<StoreBadges />
					</FeatureContent>
					<HeroImage src={GirlStudying} alt='Girl Studying' />
			</Hero>
			<FeaturesWrapper
				first
				style={{
					backgroundColor: theme.background2,
				}}>
				<Feature reverse>
						<FeatureImage src={Software} alt='Software' />
						<FeatureContent>
							<FeatureTitle>Built around teaching</FeatureTitle>
							<FeatureDescription>
								Every module gets a room. Staff run office hours
								with a live queue, publish events, and check
								understanding with a poll — without asking
								students to install one more app.
							</FeatureDescription>
						</FeatureContent>
				</Feature>
			</FeaturesWrapper>
			<FeaturesWrapper>
				<Feature>
						<FeatureContent>
							<FeatureTitle>Easy to roll out</FeatureTitle>
							<FeatureDescription>
								Nobody creates another account. Students and
								staff sign in with their university email, and
								rooms for a whole intake can be created in one
								step rather than one at a time.
							</FeatureDescription>
						</FeatureContent>
						<FeatureImage src={Lecture} alt='Lecture' />
				</Feature>
			</FeaturesWrapper>
			<FeaturesWrapper
				style={{
					backgroundColor: theme.background2,
				}}>
				<Feature reverse>
						<FeatureImage src={Price} alt='Price' />
						<FeatureContent>
							<FeatureTitle>Priced for institutions</FeatureTitle>
							<FeatureDescription>
								Straightforward per-institution pricing with no
								per-feature upsells — every capability listed on
								this page is included.
							</FeatureDescription>
							<Button
								onClick={() =>
									props.history.push(routes.PRICING)
								}
								size='medium'
								type='secondary'>
								View our plans
							</Button>
						</FeatureContent>
				</Feature>
			</FeaturesWrapper>
			<Section $alt>
				<SectionInner>
					<SectionBody>
						<SectionHeader>
							<SectionEyebrow>{HIGHLIGHTS.eyebrow}</SectionEyebrow>
							<SectionTitle>{HIGHLIGHTS.title}</SectionTitle>
							<SectionSubtitle>
								{HIGHLIGHTS.subtitle}
							</SectionSubtitle>
						</SectionHeader>
						<Grid>
							{HIGHLIGHTS.features.map((feature) => (
								<Item key={feature.title}>
									<ItemTitle>{feature.title}</ItemTitle>
									<ItemText>{feature.text}</ItemText>
								</Item>
							))}
						</Grid>
						<PlatformNote>
							This is the short version.{' '}
							<CatalogueLink
								onClick={() =>
									props.history.push(routes.WHY_FLASS)
								}>
								See every feature on Why Flass
							</CatalogueLink>
							.
						</PlatformNote>
					</SectionBody>
				</SectionInner>
			</Section>

			<FeaturesWrapper>
				<Feature>
						<FeatureContent style={{ maxWidth: '500px' }}>
							<div
								ref={contactFormRef}
								style={{ height: '0px' }}
							/>
							<FeatureTitle>
								Bring Flass to your university
							</FeatureTitle>
							<ContactForm
								style={{ marginTop: '15px', width: '100%' }}
							/>
						</FeatureContent>
						<FeatureImage src={Mail} alt='Mail' />
				</Feature>
			</FeaturesWrapper>
			<Copyright />
		</Wrapper>
	);
};

export default Landing;
